// Minimal chess helpers: enough to draw positions, play scripted moves and
// show move hints. Positions are plain objects of square -> piece ('K' white, 'k' black).

export const FILES = 'abcdefgh';
export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export const SOLID = { k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟' };
export const OUTLINE = { k: '♔', q: '♕', r: '♖', b: '♗', n: '♘', p: '♙' };
export const PIECE_NAMES = { k: 'King', q: 'Queen', r: 'Rook', b: 'Bishop', n: 'Knight', p: 'Pawn' };

export const isWhite = (piece) => piece === piece.toUpperCase();

const toXY = (sq) => [FILES.indexOf(sq[0]), Number(sq[1]) - 1];
const toSq = (x, y) => (x >= 0 && x < 8 && y >= 0 && y < 8 ? FILES[x] + (y + 1) : null);

export function fromFen(fen) {
  const position = {};
  fen.split(' ')[0].split('/').forEach((row, i) => {
    let x = 0;
    for (const ch of row) {
      if (/\d/.test(ch)) {
        x += Number(ch);
      } else {
        position[FILES[x] + (8 - i)] = ch;
        x += 1;
      }
    }
  });
  return position;
}

export function applyMove(position, move) {
  const next = { ...position };
  const piece = next[move.from];
  delete next[move.from];
  next[move.to] = move.promo ? (isWhite(piece) ? move.promo.toUpperCase() : move.promo) : piece;
  if (move.rook) {
    next[move.rook.to] = next[move.rook.from];
    delete next[move.rook.from];
  }
  return next;
}

const LINES = {
  r: [[1, 0], [-1, 0], [0, 1], [0, -1]],
  b: [[1, 1], [1, -1], [-1, 1], [-1, -1]],
};
LINES.q = [...LINES.r, ...LINES.b];
const STEPS = {
  n: [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]],
  k: LINES.q,
};

// Pseudo-legal destinations (ignores checks and castling): good enough for hints.
export function movesFrom(position, sq) {
  const piece = position[sq];
  if (!piece) return [];
  const white = isWhite(piece);
  const type = piece.toLowerCase();
  const [x, y] = toXY(sq);
  const own = (s) => position[s] && isWhite(position[s]) === white;
  const enemy = (s) => position[s] && isWhite(position[s]) !== white;
  const out = [];

  if (type === 'p') {
    const dir = white ? 1 : -1;
    const one = toSq(x, y + dir);
    if (one && !position[one]) {
      out.push(one);
      const two = toSq(x, y + 2 * dir);
      if ((white ? y === 1 : y === 6) && two && !position[two]) out.push(two);
    }
    [-1, 1].forEach((dx) => {
      const s = toSq(x + dx, y + dir);
      if (s && enemy(s)) out.push(s);
    });
    return out;
  }

  if (type === 'n' || type === 'k') {
    STEPS[type].forEach(([dx, dy]) => {
      const s = toSq(x + dx, y + dy);
      if (s && !own(s)) out.push(s);
    });
    return out;
  }

  LINES[type].forEach(([dx, dy]) => {
    for (let i = 1; i < 8; i += 1) {
      const s = toSq(x + dx * i, y + dy * i);
      if (!s || own(s)) break;
      out.push(s);
      if (position[s]) break;
    }
  });
  return out;
}

function attacksFrom(position, sq) {
  const piece = position[sq];
  if (piece.toLowerCase() !== 'p') return movesFrom(position, sq);
  const [x, y] = toXY(sq);
  const dir = isWhite(piece) ? 1 : -1;
  return [toSq(x - 1, y + dir), toSq(x + 1, y + dir)].filter(Boolean);
}

export function inCheck(position, white) {
  const king = Object.keys(position).find((s) => position[s] === (white ? 'K' : 'k'));
  return Object.keys(position).some(
    (s) => isWhite(position[s]) !== white && attacksFrom(position, s).includes(king)
  );
}

// Pawns reaching the last rank promote to a queen.
export function withPromotion(position, move) {
  const piece = position[move.from];
  const lastRank = move.to[1] === '8' || move.to[1] === '1';
  return piece && piece.toLowerCase() === 'p' && lastRank && !move.promo ? { ...move, promo: 'q' } : move;
}

export function hasLegalMove(position, white) {
  return Object.keys(position).some(
    (s) =>
      isWhite(position[s]) === white &&
      movesFrom(position, s).some((t) => {
        if (position[t] && position[t].toLowerCase() === 'k') return false;
        return !inCheck(applyMove(position, withPromotion(position, { from: s, to: t })), white);
      })
  );
}

export const isCheckmate = (position, white) => inCheck(position, white) && !hasLegalMove(position, white);

export function toSan(position, move) {
  const piece = position[move.from];
  const type = piece.toLowerCase();
  const capture = position[move.to] ? 'x' : '';
  if (type === 'p') {
    return `${capture ? move.from[0] + 'x' : ''}${move.to}${move.promo ? '=' + move.promo.toUpperCase() : ''}`;
  }
  return `${type.toUpperCase()}${capture}${move.to}`;
}

export function squaresInOrder() {
  const squares = [];
  for (let rank = 8; rank >= 1; rank -= 1) {
    for (let f = 0; f < 8; f += 1) squares.push(FILES[f] + rank);
  }
  return squares;
}

export const isDark = (sq) => {
  const [x, y] = toXY(sq);
  return (x + y) % 2 === 0;
};
